import type { TransferContactInfoProps } from './index.types';
import { Icon } from 'Components/shared/Icon';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React from 'react';
import { Avatar, AvatarFallback } from '~/lib/ui/avatar';

/**
 * TransferContactInfo - Displays the selected transfer contact's identity
 * with avatar, name, contact type, and a cancel button.
 */
const TransferContactInfo = ({
  onCancel,
  testID,
}: TransferContactInfoProps): React.ReactElement | null => {
  const componentTestId = testID ? `${testID}-contact-info` : 'contact-info';

  const transferTarget = useSoftphoneStore((state) => state.transferTarget);

  if (!transferTarget) {
    return null;
  }

  const isDirectNumber = transferTarget.type === 'number';

  const contactTypeLabel = isDirectNumber
    ? null
    : transferTarget.contactType === 'directory'
      ? 'Directory contact'
      : 'External contact';

  return (
    <div
      className="flex flex-row items-center gap-[8px]"
      data-testid={componentTestId}
      role="group"
      aria-label={`Transfer target: ${transferTarget.displayName}`}
    >
      <Avatar className="size-[30px] flex-shrink-0">
        {isDirectNumber ? (
          <AvatarFallback className="bg-foreground">
            <Icon icon="user" className="!text-white" />
          </AvatarFallback>
        ) : (
          <AvatarFallback name={transferTarget.displayName} />
        )}
      </Avatar>

      <div className="flex flex-col min-w-0 flex-1">
        {transferTarget.displayName && (
          <span
            className="text-[16px] leading-[22px] font-normal text-foreground truncate"
            data-testid={`${componentTestId}-name`}
          >
            {transferTarget.displayName}
          </span>
        )}

        {contactTypeLabel && (
          <span
            className="text-[12px] leading-[16px] font-normal text-foreground truncate"
            data-testid={`${componentTestId}-contact-type`}
          >
            {contactTypeLabel}
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={onCancel}
        className="flex items-center justify-center flex-shrink-0 cursor-pointer bg-transparent border-none p-1"
        aria-label="Cancel transfer"
        data-testid={`${componentTestId}-cancel`}
      >
        <Icon icon="cross" size="tiny" />
      </button>
    </div>
  );
};

TransferContactInfo.displayName = 'TransferContactInfo';

export { TransferContactInfo };
